import {useMemo, useState} from 'react';
import {
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList
} from '@kroll-resolver-crisp/ui-components-react';
import {X} from 'lucide-react';
import {type ItemFilters, type RdOptionsScope} from '@/api/items';
import {useRdSignalNames, useRdSignalValues} from '@/api/hooks';
import {formatRdSignalName, formatRdSignalValue as formatRiskSignalValue} from '@/lib/rdSignals';
import {useDebounce} from '@/lib/useDebounce';
import {type RdFilter} from '@/store/uiStore';

function formatScalarValue(valueJson: string): string {
  if (valueJson === 'true') {
    return 'Yes';
  }

  if (valueJson === 'false') {
    return 'No';
  }

  if (valueJson.startsWith('"') && valueJson.endsWith('"')) {
    try {
      const value: unknown = JSON.parse(valueJson);
      if (typeof value === 'string') {
        if (value === 'YES' || value === 'NO') {
          return value === 'YES' ? 'Yes' : 'No';
        }

        return /^(?:true|false|null|-?(?:0|[1-9]\d*)(?:\.\d+)?(?:e[+-]?\d+)?)$/i.test(value)
          ? valueJson
          : value;
      }
    } catch {
      return valueJson;
    }
  }

  return valueJson;
}

export function formatRdSignalValue(valueJson: string, signal?: string): string {
  const scalar = formatScalarValue(valueJson);
  return signal?.startsWith('risk:') ? formatRiskSignalValue(scalar, signal) : scalar;
}

type Props = {
  readonly scope: RdOptionsScope;
  // Options reflect metadata filters, never the selected RD groups.
  readonly filters: ItemFilters;
  readonly selections: RdFilter[];
  readonly onChange: (patch: {rdFilters: RdFilter[]}) => void;
};

// Server-filtered options must not be filtered a second time in the browser.
const HIGHLIGHT_TOP_RESULT = 'always' as unknown as boolean;

function RdValueFilter({
  scope,
  filters,
  selection,
  onChange
}: {
  readonly scope: RdOptionsScope;
  readonly filters: ItemFilters;
  readonly selection: RdFilter;
  readonly onChange: (values: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const values = useRdSignalValues(
    scope,
    selection.signal,
    filters,
    useDebounce(search.trim(), 300)
  );
  const items = useMemo(
    () => [
      ...new Set([
        ...selection.values,
        ...(values.data?.results ?? []).map((option) => option.value_json)
      ])
    ],
    [selection.values, values.data]
  );
  const counts = useMemo(
    () => new Map((values.data?.results ?? []).map((option) => [option.value_json, option.count])),
    [values.data]
  );
  const name = formatRdSignalName(selection.signal);
  return (
    <div
      className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5"
      role="group"
      aria-label={`${name} values`}>
      <span className="text-muted-foreground text-xs">{name}:</span>
      <Combobox
        multiple
        items={items}
        filter={null}
        value={selection.values}
        itemToStringLabel={(value) => formatRdSignalValue(value, selection.signal)}
        autoHighlight={HIGHLIGHT_TOP_RESULT}
        onValueChange={onChange}
        onInputValueChange={(next, details) => {
          setSearch(details.reason === 'input-change' ? next : '');
        }}>
        <ComboboxInput
          showClear
          aria-label={`Values of ${name}`}
          placeholder={selection.values.length > 0 ? 'Add values…' : 'Any value'}
          className="w-40"
        />
        <ComboboxContent>
          <ComboboxEmpty>
            {values.isError
              ? 'Could not load signal values'
              : values.isFetching
                ? 'Searching…'
                : 'No scalar values observed'}
          </ComboboxEmpty>
          <ComboboxList>
            {(value: string) => (
              <ComboboxItem key={value} value={value} title={value}>
                <span className="break-words">{formatRdSignalValue(value, selection.signal)}</span>
                {counts.has(value) ? (
                  <span className="text-muted-foreground ml-auto pl-2 text-xs">
                    {counts.get(value)}
                  </span>
                ) : null}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {selection.values.map((value) => (
        <Button
          key={value}
          variant="outline"
          size="sm"
          title={value}
          className="h-auto max-w-full items-start py-1 text-left whitespace-normal"
          aria-label={`Remove ${formatRdSignalValue(value, selection.signal)} from ${name}`}
          onClick={() => {
            onChange(selection.values.filter((current) => current !== value));
          }}>
          <X aria-hidden className="mt-0.5 size-3 shrink-0" />
          <span className="min-w-0 break-words">
            {formatRdSignalValue(value, selection.signal)}
          </span>
        </Button>
      ))}
    </div>
  );
}

export function RdSignalFilter({scope, filters, selections, onChange}: Props) {
  const [search, setSearch] = useState('');
  const names = useRdSignalNames(scope, filters, useDebounce(search.trim(), 300), true);
  const selectedNames = useMemo(
    () => selections.map((selection) => selection.signal),
    [selections]
  );
  const items = useMemo(
    () => [
      ...new Set([...selectedNames, ...(names.data?.results ?? []).map((option) => option.name)])
    ],
    [names.data, selectedNames]
  );
  const counts = useMemo(
    () => new Map((names.data?.results ?? []).map((option) => [option.name, option.count])),
    [names.data]
  );
  return (
    <div
      className="flex basis-full flex-wrap items-center gap-1.5"
      role="group"
      aria-label="RD signal filter">
      <Combobox
        multiple
        items={items}
        filter={null}
        value={selectedNames}
        itemToStringLabel={formatRdSignalName}
        autoHighlight={HIGHLIGHT_TOP_RESULT}
        onValueChange={(next) => {
          onChange({
            rdFilters: next.map(
              (signal) =>
                selections.find((selection) => selection.signal === signal) ?? {signal, values: []}
            )
          });
        }}
        onInputValueChange={(next, details) => {
          setSearch(details.reason === 'input-change' ? next : '');
        }}>
        <ComboboxInput
          showClear
          aria-label="RD signal names"
          placeholder="RD signals"
          className="w-44"
        />
        <ComboboxContent>
          <ComboboxEmpty>
            {names.isError
              ? 'Could not load signals'
              : names.isFetching
                ? 'Searching…'
                : 'No signals observed'}
          </ComboboxEmpty>
          <ComboboxList>
            {(name: string) => (
              <ComboboxItem key={name} value={name}>
                <span className="break-words">{formatRdSignalName(name)}</span>
                {counts.has(name) ? (
                  <span className="text-muted-foreground ml-auto pl-2 text-xs">
                    {counts.get(name)}
                  </span>
                ) : null}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {selections.length > 0 ? (
        <>
          <span className="text-muted-foreground text-xs">
            Match any signal / any selected value
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange({rdFilters: []});
            }}>
            Clear RD filters
          </Button>
        </>
      ) : null}
      {selections.map((selection) => (
        <div
          key={selection.signal}
          className="border-border flex basis-full items-start gap-1.5 border-l-2 pl-2">
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label={`Remove ${formatRdSignalName(selection.signal)} signal`}
            onClick={() => {
              onChange({
                rdFilters: selections.filter((current) => current.signal !== selection.signal)
              });
            }}>
            <X aria-hidden className="size-3" />
          </Button>
          <RdValueFilter
            scope={scope}
            filters={filters}
            selection={selection}
            onChange={(values) => {
              onChange({
                rdFilters: selections.map((current) =>
                  current.signal === selection.signal ? {...current, values} : current
                )
              });
            }}
          />
        </div>
      ))}
    </div>
  );
}
